// Phase 16 — Interactive Ecosystem Role Diagram (authenticated dashboard)
//
// Cosmic 5-node diagram with:
//   • Customer · Affiliate · Driver · Partner · Franchise nodes around a
//     "LIFTED ECOSYSTEM" centre.
//   • Click a node → node lights up, animated dashed line glows the active
//     spoke from the centre, side panel reveals role description + tools +
//     training + dashboard CTA.
//   • If the logged-in user has that role → "Open [Role] Zone" button.
//     If not → public explanation or apply CTA (where applicable).
//   • Mobile fallback: stacked card grid below the diagram.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag, Rocket, Truck, Store, Building2, Shield,
  ArrowRight, X, ExternalLink, GraduationCap, BarChart3, MessageSquare,
} from "lucide-react";

const ROLE_NODES = [
  {
    key: "customer",
    label: "Customer",
    icon: ShoppingBag,
    angle: -90,                    // top
    title: "CUSTOMER — THE REASON WE EXIST",
    description:
      "Customers shop the Lifted ecosystem — gear, accessories, head-shop, "
      + "and same-day Supplies. Every other role exists to serve them.",
    tools: ["Shop catalog", "Same-day or shipping", "Order history", "Lifted Network feed"],
    training: "Customer access is open — no training required.",
    earn: "Loyalty rewards through affiliate referrals + future Customer perks.",
    destination: "/shop",
    publicCta: { label: "Shop the catalog", to: "/shop" },
  },
  {
    key: "affiliate",
    label: "Affiliate",
    icon: Rocket,
    angle: -18,
    title: "AFFILIATE — THE HEARTBEAT",
    description:
      "Affiliates are the marketing engine. Generate referral links + QR codes, "
      + "track conversions, earn commissions, and unlock higher tiers as your "
      + "network grows.",
    tools: ["Tracking dashboard", "Product Picker", "Commissions ledger", "Marketing tools"],
    training: "Required: Lifted Affiliate Academy (≥70%) for tier advancement.",
    earn: "Commission on every sale your network drives, across the full ecosystem.",
    destination: "/affiliate",
    publicCta: { label: "Apply for affiliate access", to: "/affiliate-zone" },
  },
  {
    key: "driver",
    label: "Driver",
    icon: Truck,
    angle: 54,
    title: "DRIVER — LAST-MILE LIFT",
    description:
      "Drivers complete Lifted Supplies same-day deliveries. Background screening "
      + "+ driver training required. Live map + dispatch board show your route.",
    tools: ["Driver dashboard", "Dispatch board", "Live map", "Earnings & tips"],
    training: "Required: Driver Track + safety modules + compliance review.",
    earn: "Per-delivery payouts + tips. Same-day Supplies optimized routes.",
    destination: "/delivery",
    publicCta: { label: "Apply to drive", to: "/apply/driver" },
  },
  {
    key: "partner",
    label: "Partner",
    icon: Store,
    angle: 126,
    title: "PARTNER — STOREFRONT ALLIES",
    description:
      "Partner stores plug into the Lifted ecosystem. Upload approved products, "
      + "receive ingested orders, manage compliance, and tap affiliate-driven "
      + "exposure.",
    tools: ["Partner dashboard", "Product upload", "Order queue", "Compliance tracker"],
    training: "Required: Partner Track + compliance + brand standards.",
    earn: "Revenue share on every Lifted-routed sale. Partner discount stack.",
    destination: "/partner",
    publicCta: { label: "Partner with us", to: "/apply/partner" },
  },
  {
    key: "franchisee",
    label: "Franchise",
    icon: Building2,
    angle: 198,
    title: "FRANCHISE — LONG-TERM TERRITORY",
    description:
      "Franchisees expand Lifted into a new market. Manage territory inventory, "
      + "drivers, partners, and compliance — your zone, the whole stack.",
    tools: ["Franchise dashboard", "Territory map", "Driver roster", "Inventory + orders"],
    training: "Required: Franchise Track + ecosystem ops + dispatch playbook.",
    earn: "Territory revenue share + Lifted brand growth across your market.",
    destination: "/franchise",
    publicCta: { label: "Explore franchise", to: "/apply/franchise" },
  },
];

const ADMIN_NODE = {
  key: "admin",
  label: "Admin",
  icon: Shield,
  // Will be appended only when the viewer has admin/master_admin and we use
  // it as a "centre-right" anchor near the top.
  angle: 270,                       // bottom — different from any role
  title: "ADMIN · OPERATIONS",
  description:
    "Admin & Master Admin run the platform: compliance, billing, role gates, "
    + "training certification, network moderation, dispatch oversight.",
  tools: ["Command Center", "Affiliate Billing", "System Readiness", "User Management"],
  training: "Internal access — admin policies apply.",
  earn: "Platform operator.",
  destination: "/admin",
  publicCta: null,
};

const RADIUS = 170;
const CENTER = 250;

function hasRole(me, key) {
  if (!me) return false;
  const r = me.roles || [];
  if (key === "admin") return r.includes("admin") || r.includes("master_admin");
  return r.includes(key);
}

export default function EcosystemRoleDiagram({ me, includeAdmin = false }) {
  const [active, setActive] = useState(null);

  const nodes = useMemo(() => {
    const arr = [...ROLE_NODES];
    if (includeAdmin && hasRole(me, "admin")) arr.push(ADMIN_NODE);
    return arr;
  }, [me, includeAdmin]);

  return (
    <section className="glass-panel p-6 lg:p-8 border border-white/10"
             data-testid="ecosystem-role-diagram">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Diagram (left 7/12) */}
        <div className="lg:col-span-7 flex justify-center">
          <div className="relative w-full max-w-[520px] aspect-square">
            {/* Cosmic glow */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(184,255,103,0.18) 0%, rgba(139,63,194,0.22) 38%, rgba(11,11,16,0) 72%)",
              }}
              aria-hidden="true"
            />

            <svg viewBox="0 0 500 500" className="absolute inset-0 w-full h-full" aria-hidden="true">
              <defs>
                <radialGradient id="ed-centerGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#0B0B10" />
                  <stop offset="100%" stopColor="#15131D" />
                </radialGradient>
                <filter id="ed-glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <style>{`
                  @keyframes ed-dash { to { stroke-dashoffset: -200; } }
                  .ed-active-spoke {
                    stroke-dasharray: 6 8;
                    animation: ed-dash 2.2s linear infinite;
                  }
                `}</style>
              </defs>

              {/* Orbit ring */}
              <circle cx={CENTER} cy={CENTER} r={RADIUS}
                      fill="none" stroke="#B8FF67" strokeOpacity="0.35"
                      strokeWidth="1" strokeDasharray="2 6" />
              <circle cx={CENTER} cy={CENTER} r={RADIUS + 22}
                      fill="none" stroke="#8B3FC2" strokeOpacity="0.18"
                      strokeWidth="0.75" />

              {/* Spokes */}
              {nodes.map((n) => {
                const rad = (n.angle * Math.PI) / 180;
                const x = CENTER + Math.cos(rad) * RADIUS;
                const y = CENTER + Math.sin(rad) * RADIUS;
                const isActive = active?.key === n.key;
                return (
                  <line key={`spoke-${n.key}`}
                        x1={CENTER} y1={CENTER} x2={x} y2={y}
                        stroke={isActive ? "#B8FF67" : "#B8FF67"}
                        strokeOpacity={isActive ? 0.85 : 0.12}
                        strokeWidth={isActive ? 1.5 : 0.8}
                        className={isActive ? "ed-active-spoke" : ""} />
                );
              })}

              {/* Center pill */}
              <g filter="url(#ed-glow)">
                <ellipse cx={CENTER} cy={CENTER} rx={94} ry={42}
                         fill="url(#ed-centerGrad)" stroke="#B8FF67" strokeWidth="1.2" />
              </g>
              <text x={CENTER} y={CENTER - 4} textAnchor="middle"
                    fontFamily="Anton, Bebas Neue, sans-serif" fontSize="14"
                    letterSpacing="2.5" fill="#F3F5F0">LIFTED</text>
              <text x={CENTER} y={CENTER + 14} textAnchor="middle"
                    fontFamily="Anton, Bebas Neue, sans-serif" fontSize="11"
                    letterSpacing="2.2" fill="#B8FF67">ECOSYSTEM</text>

              {/* Role nodes */}
              {nodes.map((n) => {
                const rad = (n.angle * Math.PI) / 180;
                const x = CENTER + Math.cos(rad) * RADIUS;
                const y = CENTER + Math.sin(rad) * RADIUS;
                const isActive = active?.key === n.key;
                const userHas = hasRole(me, n.key);
                return (
                  <g key={n.key} style={{ cursor: "pointer" }}
                     onClick={() => setActive(n)}
                     data-testid={`ed-node-${n.key}`}>
                    <circle cx={x} cy={y} r={isActive ? 44 : 36}
                            fill="#B8FF67"
                            fillOpacity={isActive ? 0.28 : (userHas ? 0.15 : 0.08)} />
                    <circle cx={x} cy={y} r={26} fill="#0B0B10"
                            stroke={isActive ? "#B8FF67" : (userHas ? "#B8FF67" : "#8B3FC2")}
                            strokeWidth={isActive ? 1.8 : 1} />
                    <text x={x} y={y + 50} textAnchor="middle"
                          fontFamily="Anton, Bebas Neue, sans-serif"
                          fontSize="11" letterSpacing="2"
                          fill={isActive ? "#B8FF67" : "#F3F5F0"}>
                      {n.label.toUpperCase()}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Icon overlays */}
            {nodes.map((n) => {
              const rad = (n.angle * Math.PI) / 180;
              const xPct = ((CENTER + Math.cos(rad) * RADIUS) / 500) * 100;
              const yPct = ((CENTER + Math.sin(rad) * RADIUS) / 500) * 100;
              const Icon = n.icon;
              const isActive = active?.key === n.key;
              return (
                <button
                  type="button"
                  key={`icon-${n.key}`}
                  onClick={() => setActive(n)}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full grid place-items-center transition ${
                    isActive ? "scale-110" : "hover:scale-105"
                  }`}
                  style={{ left: `${xPct}%`, top: `${yPct}%` }}
                  aria-label={n.label}
                  data-testid={`ed-node-icon-${n.key}`}
                >
                  <Icon className={`h-4 w-4 ${isActive ? "text-lifted-green" : "text-white/85"}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail (right 5/12) */}
        <div className="lg:col-span-5">
          {active ? (
            <RoleDetail node={active} me={me} onClose={() => setActive(null)} />
          ) : (
            <DiagramIntro nodes={nodes} onSelect={setActive} />
          )}
        </div>
      </div>

      {/* Mobile stacked cards (always visible below the diagram) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-6 lg:hidden"
           data-testid="ed-mobile-cards">
        {nodes.map((n) => {
          const Icon = n.icon;
          return (
            <button key={n.key} onClick={() => setActive(n)}
                    className={`glass-panel p-3 text-left ${
                      active?.key === n.key ? "border-lifted-green/60" : "border-white/10"
                    } border`}
                    data-testid={`ed-card-${n.key}`}>
              <Icon className="h-4 w-4 text-lifted-green mb-1" />
              <div className="font-bebas tracking-[0.18em] text-[0.7rem] uppercase text-white">
                {n.label}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function DiagramIntro({ nodes, onSelect }) {
  return (
    <div>
      <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green uppercase">
        The Lifted Ecosystem
      </div>
      <h2 className="font-anton text-2xl sm:text-3xl uppercase mt-2 leading-[0.95] text-white">
        Tap any role to <span className="text-lifted-green">explore.</span>
      </h2>
      <p className="text-lifted-dim text-sm mt-3 max-w-md">
        Lifted at the centre. Customers, Affiliates, Drivers, Partners, and
        Franchise orbit it — each amplifying the others.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2 hidden lg:grid"
           data-testid="ed-intro-list">
        {nodes.map((n) => {
          const Icon = n.icon;
          return (
            <button type="button" key={n.key}
                    onClick={() => onSelect(n)}
                    className="glass-panel p-3 flex items-center gap-2 hover:-translate-y-0.5 transition-transform text-left">
              <Icon className="h-4 w-4 text-lifted-green" />
              <span className="font-bebas tracking-[0.18em] text-[0.7rem] uppercase text-white">
                {n.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RoleDetail({ node, me, onClose }) {
  const Icon = node.icon;
  const userHas = hasRole(me, node.key);

  return (
    <div className="glass-panel p-5 lg:p-6 border border-lifted-green/40"
         data-testid={`ed-detail-${node.key}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-lifted-green" />
          <div className="font-bebas tracking-[0.22em] text-sm text-white">
            {node.title}
          </div>
        </div>
        <button onClick={onClose}
                className="text-lifted-dim hover:text-white"
                aria-label="Close role detail"
                data-testid={`ed-detail-close-${node.key}`}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <p className="text-lifted-text text-sm leading-relaxed">{node.description}</p>

      <div className="grid grid-cols-1 gap-3 mt-4">
        <RoleRow icon={BarChart3} title="Tools you get">
          <ul className="list-disc list-inside text-lifted-dim text-xs space-y-0.5">
            {node.tools.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </RoleRow>
        <RoleRow icon={GraduationCap} title="Training / certification">
          <p className="text-lifted-dim text-xs">{node.training}</p>
        </RoleRow>
        <RoleRow icon={MessageSquare} title="How you earn / participate">
          <p className="text-lifted-dim text-xs">{node.earn}</p>
        </RoleRow>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {userHas ? (
          <Link to={node.destination}
                className="btn-lifted inline-flex items-center gap-1 text-xs"
                data-testid={`ed-detail-open-${node.key}`}>
            Open {node.label} Zone <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        ) : node.publicCta ? (
          <Link to={node.publicCta.to}
                className="btn-lifted-ghost inline-flex items-center gap-1 text-xs"
                data-testid={`ed-detail-cta-${node.key}`}>
            {node.publicCta.label} <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        ) : null}
        {userHas && (
          <span className="text-[0.65rem] text-lifted-green font-bebas tracking-[0.18em] uppercase">
            You have this role
          </span>
        )}
      </div>
    </div>
  );
}

function RoleRow({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="font-bebas tracking-[0.2em] text-[0.6rem] uppercase text-lifted-green flex items-center gap-1.5 mb-1">
        <Icon className="h-3 w-3" /> {title}
      </div>
      {children}
    </div>
  );
}
